import {
  type CSSProperties,
  type ReactNode,
  type RefCallback,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  type PanInfo,
  m,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'motion/react'
import { animate } from 'motion'
import cx from 'classnames'
import { IconButton } from '@/components/IconButton'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useElementSize } from '@/hooks/element-hooks'
import flattenChildren from 'react-keyed-flatten-children'
import { carouselBounceSpring, carouselSpring } from '@/consts/springs'
import styles from './Carousel.module.css'

const SWIPE_OFFSET_THRESHOLD = 50
const SWIPE_VELOCITY_THRESHOLD = 300
const FADE_RANGE = 80

type CarouselNavigationArrowAlignmentRef = RefCallback<Element> | null
const CarouselNavigationArrowAlignmentRefContext =
  createContext<CarouselNavigationArrowAlignmentRef>(null)

export function useCarouselNavigationArrowAlignmentRef(): CarouselNavigationArrowAlignmentRef {
  return useContext(CarouselNavigationArrowAlignmentRefContext)
}

export interface CarouselProps {
  children?: ReactNode
  testId?: string
}

export function Carousel({ children, testId }: CarouselProps) {
  const isSpacious = useIsSpacious()
  const items = flattenChildren(children)
  const total = items.length
  const {
    activeIndex,
    next,
    prev,
    goTo,
    isFirst,
    isLast,
    x,
    trackRef,
    dragConstraints,
    dragElastic,
    handleDragEnd,
    maskImage,
  } = useCarousel(total)
  const isShowingNavigation = total > 1
  const { connectRef: connectArrowAlignRef, size: arrowAlignSize } =
    useElementSize()

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label="Carousel"
      data-test={testId}
      style={
        arrowAlignSize.height
          ? ({
              '--carousel-arrow-align-height': `${arrowAlignSize.height}px`,
            } as CSSProperties)
          : undefined
      }
    >
      <div className={styles.container}>
        {isSpacious && isShowingNavigation && (
          <div className={styles.arrowButton}>
            <CarouselArrow
              direction="prev"
              onPress={prev}
              isDisabled={isFirst}
            />
          </div>
        )}
        <div className={styles.trackWrapper}>
          <m.div
            ref={trackRef}
            className={styles.track}
            style={{ maskImage }}
            onPointerDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
          >
            <m.div
              className={styles.strip}
              style={{ x }}
              drag="x"
              dragDirectionLock
              dragMomentum={false}
              dragConstraints={dragConstraints}
              dragElastic={dragElastic}
              onDragEnd={handleDragEnd}
            >
              {items.map((item, i) => (
                <div key={i} className={styles.slide}>
                  <CarouselNavigationArrowAlignmentRefContext
                    value={i === activeIndex ? connectArrowAlignRef : null}
                  >
                    {item}
                  </CarouselNavigationArrowAlignmentRefContext>
                </div>
              ))}
            </m.div>
          </m.div>
          {!isSpacious && isShowingNavigation && (
            <>
              <div className={cx(styles.overlayArrow, styles.overlayArrowLeft)}>
                <CarouselArrow
                  direction="prev"
                  onPress={prev}
                  isDisabled={isFirst}
                />
              </div>
              <div
                className={cx(styles.overlayArrow, styles.overlayArrowRight)}
              >
                <CarouselArrow
                  direction="next"
                  onPress={next}
                  isDisabled={isLast}
                />
              </div>
            </>
          )}
        </div>
        {isSpacious && isShowingNavigation && (
          <div className={styles.arrowButton}>
            <CarouselArrow
              direction="next"
              onPress={next}
              isDisabled={isLast}
            />
          </div>
        )}
      </div>
      {isShowingNavigation && (
        <div className={styles.paginationDots}>
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              className={cx(styles.dot, i === activeIndex && styles.dotActive)}
              onClick={() => goTo(i)}
              aria-label={`Go to step ${i + 1} of ${total}`}
              aria-current={i === activeIndex ? 'step' : undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function useCarousel(total: number) {
  const [activeIndex, setActiveIndex] = useState(0)
  const { connectRef, size } = useElementSize()

  const x = useMotionValue(0)

  const next = useCallback(() => {
    setActiveIndex((i) => (i >= total - 1 ? i : i + 1))
  }, [total])

  const prev = useCallback(() => {
    setActiveIndex((i) => (i <= 0 ? i : i - 1))
  }, [])

  const goTo = useCallback((index: number) => {
    setActiveIndex(index)
  }, [])

  const isFirst = activeIndex === 0
  const isLast = activeIndex === total - 1

  useEffect(() => {
    if (!size.width) {
      return
    }
    animate(x, -activeIndex * size.width, carouselSpring)
  }, [activeIndex, size.width, x])

  const dragElastic = {
    left: isLast ? 0.08 : 0.15,
    right: isFirst ? 0.08 : 0.15,
  }

  const handleDragEnd = useCallback(
    (
      _e: MouseEvent | TouchEvent | PointerEvent,
      { offset, velocity }: PanInfo,
    ) => {
      if (!size.width) {
        return
      }

      let targetIndex = activeIndex

      if (
        offset.x < -SWIPE_OFFSET_THRESHOLD ||
        velocity.x < -SWIPE_VELOCITY_THRESHOLD
      ) {
        targetIndex = Math.min(activeIndex + 1, total - 1)
      } else if (
        offset.x > SWIPE_OFFSET_THRESHOLD ||
        velocity.x > SWIPE_VELOCITY_THRESHOLD
      ) {
        targetIndex = Math.max(activeIndex - 1, 0)
      }

      const isEdgeBounce =
        targetIndex === activeIndex &&
        ((isFirst && offset.x > 0) || (isLast && offset.x < 0))

      if (targetIndex !== activeIndex) {
        goTo(targetIndex)
      } else {
        animate(
          x,
          -activeIndex * size.width,
          isEdgeBounce ? carouselBounceSpring : carouselSpring,
        )
      }
    },
    [activeIndex, total, goTo, x, size.width, isFirst, isLast],
  )

  const dragConstraints = size.width
    ? {
        left: -(activeIndex + (isLast ? 0 : 1)) * size.width,
        right: -(activeIndex - (isFirst ? 0 : 1)) * size.width,
      }
    : { left: 0, right: 0 }

  const leftFade = useTransform(x, (xVal) => {
    if (!size.width) {
      return 0
    }
    const displacement = xVal + activeIndex * size.width
    if (displacement <= 0) {
      return Math.min(Math.abs(displacement) / FADE_RANGE, 1) * 100
    }
    return 0
  })

  const rightFade = useTransform(x, (xVal) => {
    if (!size.width) {
      return 0
    }
    const displacement = xVal + activeIndex * size.width
    if (displacement >= 0) {
      return Math.min(displacement / FADE_RANGE, 1) * 100
    }
    return 0
  })

  const maskImage = useMotionTemplate`linear-gradient(
    to right,
    color-mix(in srgb, white, transparent ${leftFade}%) 0%,
    white 10%,
    white 90%,
    color-mix(in srgb, white, transparent ${rightFade}%)
  )`

  return {
    activeIndex,
    next,
    prev,
    goTo,
    isFirst,
    isLast,
    x,
    trackRef: connectRef,
    dragConstraints,
    dragElastic,
    handleDragEnd,
    maskImage,
  }
}

interface CarouselArrowProps {
  direction: 'prev' | 'next'
  onPress: () => unknown
  isDisabled: boolean
}

function CarouselArrow({ direction, onPress, isDisabled }: CarouselArrowProps) {
  return (
    <IconButton
      name="chevron"
      rotate={direction === 'prev' ? 180 : undefined}
      label={direction === 'prev' ? 'Previous' : 'Next'}
      onPress={onPress}
      isDisabled={isDisabled}
    />
  )
}
